import { FC, useContext, useMemo } from "react";
import moment from "moment";

import useMediaQuery from "@mui/material/useMediaQuery";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { DATETIME_FORMAT } from "../../constants/dateFormats";
import { submitFormOnEnter } from "../DatePicker/fieldEvents";

import FormContext from "./FormContext";

const { IO_FORMAT, DISPLAY_FORMAT } = DATETIME_FORMAT;

interface DateTimePickerCustomProps {
  label: string;
  name: string;
  disableFuture?: boolean;
  disabled?: boolean;
}

const DateTimePickerCustom: FC<DateTimePickerCustomProps> = ({
  label,
  name,
  disableFuture = false,
  disabled = false,
}) => {
  const context = useContext(FormContext);

  // A complete but impossible date-time (31/02) arrives as an invalid moment and is stored as moment's
  // "Invalid date" on purpose: the field gets an invalid value back and keeps what was typed (null
  // would clear every section), and Validation.verifyDatetimeFormat rejects it.
  const change = (momentDate: moment.Moment | null) => {
    context.set(name, momentDate ? momentDate.format(IO_FORMAT) : null);
  };

  // DateTimePicker renders its desktop variant for a fine pointer (its default desktopModeMediaQuery).
  const isDesktop = useMediaQuery("@media (pointer: fine)", { defaultMatches: true });

  const { value, error } = context.get(name);
  // Memoised so the field only re-syncs its sections when the value really changes.
  const dateValue = useMemo(
    () => (value ? (moment.isMoment(value) ? value : moment(value, IO_FORMAT)) : null),
    [value],
  );
  return (
    <DateTimePicker
      format={DISPLAY_FORMAT}
      value={dateValue}
      disableFuture={disableFuture}
      disabled={disabled}
      label={label}
      onChange={change}
      ampm={false}
      // On desktop, keep the pickers 7 behaviour: a single OK button, and the popper closes once the
      // time is picked (pickers 9 default to Cancel + Next/OK and stay open). The mobile dialog keeps
      // the pickers 9 defaults, which it needs to step from the date to the time.
      closeOnSelect={isDesktop}
      slotProps={{
        actionBar: (ownerState) => (ownerState.pickerVariant === "desktop" ? { actions: ["accept"] } : {}),
        textField: {
          helperText: DISPLAY_FORMAT.toLowerCase(),
          fullWidth: true,
          size: "small",
          error: !!error,
          onKeyDown: submitFormOnEnter,
          ...(error ? { helperText: error } : {}),
        },
      }}
    />
  );
};

export default DateTimePickerCustom;
